/**
 * NavUser.js
 * Created by calhosh on 3/14/2022.
 */

import React, {Fragment, useCallback} from 'react';
import styled from 'styled-components';
import {MotifDropdown, MotifDropdownItem, MotifIconButton} from '../MotifWrapperComponents/motifCompat';
import {labels} from '../../utils/utils';
import Ellipses from '../EllipsesPresets/EllipsesPresets';
import Env from '../../utils/env';
import DelegateAccessModal from '../../../features/users/components/DelegateAccess/DelegateAccessModal';
import {useNavigate} from 'react-router-dom';
import {toggleUnsavedModal} from '../UnsavedChangesModal/UnsavedChangesModal';
import {modalSource} from '../../utils/uiconstants';
import {useSelector} from 'react-redux';
import {adobeAnalytics} from '@ey/voltron-analytics';
import {clickActionArgs} from '../AdobeAnalytics/actionHelper';
import {clickActionNames, controlTypes, eventTypes, locationInPages, webInteractionTypes} from '../AdobeAnalytics/constants';
import MSTeamsUser from '../TeamUser/MSTeamsUser';

const NavUser = ({validEconomicsUser, validAdminUser, user, admin, isOpen, setIsOpen, isModalVisible, setModalVisibility, hasChanges, partitionKey}) => {
	const navigate = useNavigate();
	const paansData = useSelector((store) => store.paansuserdata);
	const trackEvent = adobeAnalytics.useAdobeAnalyticsTrack();

	const trackAdobeAnalyticsClickEvents = (name, url) => {
		const interactionType = url && name !== clickActionNames.userSettings ? webInteractionTypes.exit : webInteractionTypes.other;
		// Adobe Analytics Click Tracking
		trackEvent(eventTypes.clickAction, clickActionArgs(name, controlTypes.dropdown, locationInPages.headerRightNav, url, interactionType));
	};

	const renderUserTile = (currentUser) => {
		if (!currentUser || !currentUser.id) return null;
		const firstName = currentUser.firstName || '';
		const lastName = currentUser.lastName || '';
		const emailAddress = currentUser.emailAddress || '';
		const userInital = `${firstName.slice(0, 1)}${lastName.slice(0, 1)}`.toUpperCase();
		return <MSTeamsUser firstName={firstName} lastName={lastName} emailAddress={emailAddress} userInital={userInital} />;
	};

	const showOptions = useCallback(() => {
		if (hasChanges) {
			toggleUnsavedModal(modalSource.navUser, {partitionKey});
		} else {
			setIsOpen(!isOpen);
		}
	}, [isOpen, hasChanges]);

	const hideOption = useCallback(() => {
		setIsOpen(false);
	}, []);

	const openSettings = useCallback(() => {
		setIsOpen(!isOpen);
		trackAdobeAnalyticsClickEvents(clickActionNames.userSettings, `${window.location.origin}/settings`);
		navigate('/settings');
	}, [isOpen]);

	const openDelegates = useCallback(() => {
		setIsOpen(!isOpen);
		setModalVisibility(true);
		trackAdobeAnalyticsClickEvents(clickActionNames.manageDelegates);
	}, [isOpen]);

	const logout = useCallback(() => {
		setIsOpen(!isOpen);
		trackAdobeAnalyticsClickEvents(clickActionNames.signout, Env.getLogoutUrl());
		window.location.href = Env.getLogoutUrl();
	}, [isOpen, Env]);

	const onClickPrivacyNotice = useCallback(() => {
		setIsOpen(!isOpen);
		trackAdobeAnalyticsClickEvents(clickActionNames.privacyNotice, paansData.privacyNoticeDocumentUrl);
		window.open(paansData.privacyNoticeDocumentUrl);
	}, [isOpen]);

	return (
		<StyledNavUser className="nav-bar_right-wrapper">
			<section className="nav-bar__user-profile">
				{(validEconomicsUser || validAdminUser) && (
					<Fragment>
						<MotifDropdown
							open={isOpen}
							placement="bottom-end"
							id="userSettings"
							aria-labelledby="dropdown-trigger"
							handleOnClose={hideOption}
							closeOnOutsideClick
							trigger={
								<MotifIconButton
									type="button"
									onClick={showOptions}
									aria-controls="open-delegate-access"
									id="dropdown-trigger"
									aria-expanded={isOpen}
									aria-label="open-delegate-access"
									className="delegate-btn nav-user-trigger"
									title={labels.userOptions}
								>
									{renderUserTile(validEconomicsUser ? user : admin)}
								</MotifIconButton>
							}
						>
							<MotifDropdownItem onClick={openSettings}>
								<Ellipses.Single>{labels.userOptionsDropdown.userSettings}</Ellipses.Single>
							</MotifDropdownItem>
							{!validAdminUser ? (
								<MotifDropdownItem onClick={openDelegates}>
									<Ellipses.Single>{labels.userOptionsDropdown.delegateAccess}</Ellipses.Single>
								</MotifDropdownItem>
							) : (
								[]
							)}
							{paansData && paansData?.privacyNoticeDocumentUrl ? (
								<MotifDropdownItem onClick={onClickPrivacyNotice}>
									<Ellipses.Single>{labels.userOptionsDropdown.privacyNotice}</Ellipses.Single>
								</MotifDropdownItem>
							) : (
								[]
							)}
							<MotifDropdownItem onClick={logout}>
								<Ellipses.Single>{labels.userOptionsDropdown.signOut}</Ellipses.Single>
							</MotifDropdownItem>
						</MotifDropdown>
						<DelegateAccessModal showModal={isModalVisible} setModalVisibility={setModalVisibility} />
					</Fragment>
				)}
			</section>
		</StyledNavUser>
	);
};

export default NavUser;

const StyledNavUser = styled.section`
	.nav-user-trigger {
		background: transparent;
		border: none;
		padding: 0;
		margin: 0;
		cursor: pointer;
		outline: none;
		display: inline-flex;
		align-items: center;

		&:focus-visible {
			outline: var(--px-2) solid var(--blue-600);
			outline-offset: var(--px-2);
			border-radius: var(--px-4);
		}
	}

	.userdata.ms-user {
		display: inline-flex;
		align-items: center;

		mgt-person {
			--person-avatar-size: var(--px-40);
			--person-line1-font-size: var(--px-14);
			--person-line1-font-weight: 500;
			--person-line1-text-color: var(--neutrals-900);
			--person-line2-font-size: var(--px-12);
			--person-line2-font-weight: 300;
			--person-line2-text-color: var(--neutrals-700);
		}

		.userimghold {
			display: inline-flex;
			width: auto;
			min-width: 50px;
			margin-right: var(--px-8);
		}

		.userimg {
			border-radius: var(--px-100);
			background: var(--green-400);
			color: var(--neutrals-00white);
			width: 50px;
			height: 50px;
			display: flex;
			justify-content: center;
			align-items: center;
			text-transform: uppercase;
			font-weight: 500;
		}

		.userInfoDetails {
			text-align: left;
			max-width: var(--px-220);
		}

		.userName {
			color: var(--neutrals-1000);
		}

		.userEmail {
			color: var(--neutrals-1000);
		}
	}
`;
